import { ActivatedRoute, Router } from "@angular/router";
import { Component, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";

import { AccountService } from "src/app/services/account.service";
import { AuthService } from "../../services/auth.service";
import { SecurityProfileEnum } from "../../enums/securityProfile.enum";
import { ToastService } from "../../services/toast.service";
import { RoutePaths } from "src/app/shared/constants/routePaths.constants";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "src/app/services/translate.service";
import { PASSWORD_MIN_LENGTH, PASSWORD_POLICY_MESSAGE } from "src/app/shared/constants/passwordPolicy.constants";
import { meetsPasswordPolicy } from "src/app/shared/utils/password-policy.util";

@Component({
  standalone: false,
    selector: "app-reset-password",
    templateUrl: "./reset-password.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./reset-password.component.scss"],
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    form: UntypedFormGroup;
    userType: SecurityProfileEnum;
    newPassword = '';
    newPasswordInvalid = false;
    confirmPasswordInvalid = false;
    canSubmit = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private _fb: UntypedFormBuilder,
        private authService: AuthService,
        private toastService: ToastService,
        private router: Router,
        private route: ActivatedRoute,
        private zone: NgZone,
        private accountService: AccountService,
        private translateService: TranslateService,
    ) {
        let rememberMe = this.authService.getRememberMe();
        const currentUser = this.authService.getCurrentUser();

        if (this.authService.isLoggedIn() && currentUser && rememberMe) {
            if (this.superAdmin) {
                this.navigateToOldDashboard();
            } else {
                this.navigateToHome();
            }
        } else {
            this.revokeTokens();
        }
    }

    ngOnInit(): void {
        if (
            !this.route.snapshot.params.id ||
            !this.route.snapshot.queryParams.token
        ) {
            this.router.navigate([RoutePaths.forgotPassword]);
            setTimeout(
                () =>
                    this.toastService.showError({
                        error: "Expired token. Please create a new Reset password request.",
                    }),
                0,
            );
        }

        this.form = this._fb.group({
            id: ["", [Validators.required]],
            token: ["", [Validators.required]],
            newPassword: ["", [Validators.required]],
            confirmPassword: ["", [Validators.required]],
        });

        this.form.controls.id.setValue(this.route.snapshot.params.id);
        this.form.controls.token.setValue(
            this.route.snapshot.queryParams.token,
        );
        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshState());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // A field turns red from the first keystroke until it is valid: the new password until it meets
    // every policy rule, the confirmation until it matches.
    private refreshState(): void {
        const newPassword: string = this.f.newPassword ?? "";
        const confirmPassword: string = this.f.confirmPassword ?? "";
        const meetsPolicy = meetsPasswordPolicy(newPassword);
        this.newPassword = newPassword;
        this.newPasswordInvalid = newPassword.length > 0 && !meetsPolicy;
        this.confirmPasswordInvalid = confirmPassword.length > 0 && confirmPassword !== newPassword;
        this.canSubmit = meetsPolicy && confirmPassword === newPassword;
    }

    submit() {
        if (
            this.form.invalid ||
            this.f.newPassword != this.f.confirmPassword ||
            !meetsPasswordPolicy(this.f.newPassword)
        ) {
            if (this.form.invalid)
                this.toastService.displayErrorMessage(
                    "Please provide new password and confirm password",
                    true,
                );
            else if (this.f.newPassword != this.f.confirmPassword)
                this.toastService.displayErrorMessage(
                    "New password and confirm password don't match.",
                    true,
                );
            else
                this.toastService.displayErrorMessage(
                    this.translateService.getDynamicTranslation(PASSWORD_POLICY_MESSAGE, { count: PASSWORD_MIN_LENGTH }),
                );
            return;
        }
        this.accountService.resetPassword(this.f).subscribe(
            (res) => {
                this.toastService.showSuccess(
                    "Your password has been changed. Please login",
                    true,
                );
                this.router.navigate([RoutePaths.login]);
            },
            (error) => {
                this.toastService.showError(error);
            },
        );
    }

    get f() {
        return this.form.value;
    }

    get superAdmin() {
        const currentUser = this.authService.getCurrentUser();
        this.userType = currentUser.userType;
        return this.userType === SecurityProfileEnum.SuperAdmin;
    }

    navigateToHome() {
        this.zone.run(() => {
            const landingPage = this.authService.getLandingPageUrl();
            this.router.navigate([landingPage]);
        });
    }

    navigateToOldDashboard() {
        this.zone.run(() => {
            const landingPage = this.authService.getLandingPageUrl();
            this.router.navigate([landingPage]);
        });
    }

    revokeTokens() {
        if (this.authService.isLoggedIn()) {
            this.authService.revokeTokens().subscribe((_) => _);
        }
    }
}
